import { faEye } from '@fortawesome/free-regular-svg-icons';
import {
    faEllipsisV,
    faExchangeAlt,
    faFolderOpen,
    faPencilAlt,
    faSyncAlt,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { Popover, PopoverBody, PopoverTrigger } from '@components/common/popover/Popover';
import { CompleteTicketAction, popoverActions } from '../../constants/ticketlist.constants';
import {
    StyledAction,
    StyledActionList,
    StyledActionPopoverIcon,
    StyledPopoverli,
} from '../../constants/ticketlist.styled';
import { useActionPopover } from './useActionPopover';

export const ActionPopover = (props: any) => {
    const {
        ticket,
        onCompleteTicketOpen,
        onCompleteVerifyTicketOpen,
        onReassignTicketOpen,
        onCancelTicketOpen,
        onReopenTicketOpen,
        onAutoVerificationOpen,
    } = props;
    const intl = useIntl();
    const { completeTicket, reassignTicket, cancelTicket, viewDetails, editSummary, reopenTicket } = useActionPopover({
        onCompleteTicketOpen,
        onCompleteVerifyTicketOpen,
        onReassignTicketOpen,
        onCancelTicketOpen,
        onReopenTicketOpen,
        onAutoVerificationOpen,
    });
    return (
        <Popover>
            <PopoverTrigger>
                <StyledAction data-testid="action-button">
                    <span className="usa-sr-only">Actions</span>
                    <FontAwesomeIcon icon={faEllipsisV} />
                </StyledAction>
            </PopoverTrigger>

            <PopoverBody left="auto">
                <StyledActionList>
                    <ConditionalRendering isTrue={ticket.allowView}>
                        <StyledPopoverli data-testid="view-details" onClick={() => viewDetails(ticket.encryptedId)}>
                            <StyledActionPopoverIcon icon={faEye} />
                            {intl.formatMessage({ id: 'ticketList.actionPopover.viewDetails' })}
                        </StyledPopoverli>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={ticket.allowEdit}>
                        <StyledPopoverli data-testid="edit-summary" onClick={() => editSummary(ticket.encryptedId)}>
                            <StyledActionPopoverIcon icon={faPencilAlt} />
                            {intl.formatMessage({ id: 'ticketList.actionPopover.editSummary' })}
                        </StyledPopoverli>
                    </ConditionalRendering>
                    <ConditionalRendering
                        isTrue={ticket.allowCancel && ticket.workflowpermission !== 'TKM_WORKFLOW_VERIFY'}
                    >
                        <StyledPopoverli data-testid="cancel-ticket" onClick={() => cancelTicket(ticket.encryptedId)}>
                            <StyledActionPopoverIcon icon={faTimes} />
                            {intl.formatMessage({ id: 'ticketList.actionPopover.cancelTicket' })}
                        </StyledPopoverli>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={ticket.allowMarkAsComplete}>
                        <StyledPopoverli
                            data-testid="mark-as-complete"
                            onClick={() =>
                                completeTicket(
                                    ticket.encryptedId,
                                    ticket.ticketCategory,
                                    CompleteTicketAction.COMPLETE,
                                    ticket.isOcrFlow === true,
                                )
                            }
                        >
                            <StyledActionPopoverIcon icon={faSyncAlt} />
                            {intl.formatMessage({ id: 'ticketList.actionPopover.markAsComplete' })}
                        </StyledPopoverli>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={ticket.allowClaim}>
                        <StyledPopoverli
                            data-testid="claim-ticket"
                            onClick={() =>
                                completeTicket(ticket.encryptedId, ticket.ticketCategory, CompleteTicketAction.CLAIM)
                            }
                        >
                            <StyledActionPopoverIcon icon={faSyncAlt} />
                            {intl.formatMessage({ id: 'ticketList.actionPopover.claim' })}
                        </StyledPopoverli>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={ticket.allowReassign}>
                        <StyledPopoverli
                            data-testid="reassign-ticket"
                            onClick={() =>
                                reassignTicket(
                                    popoverActions.REASSIGN,
                                    ticket.encryptedId,
                                    ticket.ticketCategory,
                                    ticket.ticketType,
                                    ticket.queueName,
                                    ticket.assignee,
                                    ticket.queueId,
                                )
                            }
                        >
                            <StyledActionPopoverIcon icon={faExchangeAlt} />
                            {intl.formatMessage({ id: 'ticketList.actionPopover.reassignTicket' })}
                        </StyledPopoverli>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={ticket.allowAssign}>
                        <StyledPopoverli
                            data-testid="assign-ticket"
                            onClick={() =>
                                reassignTicket(
                                    popoverActions.ASSIGN,
                                    ticket.encryptedId,
                                    ticket.ticketCategory,
                                    ticket.ticketType,
                                    ticket.queueName,
                                    ticket.assignee,
                                    ticket.queueId,
                                )
                            }
                        >
                            <StyledActionPopoverIcon icon={faExchangeAlt} />
                            {intl.formatMessage({ id: 'ticketList.actionPopover.assignTicket' })}
                        </StyledPopoverli>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={ticket.allowReopen}>
                        <StyledPopoverli data-testid="reopen-ticket" onClick={() => reopenTicket(ticket.encryptedId)}>
                            <StyledActionPopoverIcon icon={faFolderOpen} />
                            {intl.formatMessage({ id: 'ticketList.actionPopover.reopen' })}
                        </StyledPopoverli>
                    </ConditionalRendering>
                </StyledActionList>
            </PopoverBody>
        </Popover>
    );
};
